import { CHARGE_TYPE, DRAWER_MODE } from '@monorepo/shared/schemas/common'
import type { PlanCharge } from '@monorepo/shared/schemas/plans'
import {
  CreditCard,
  DollarSign,
  Edit,
  Loader2,
  Plus,
  Trash2,
} from 'lucide-react'
import { useState } from 'react'
import { Badge } from '@/shared/components/ui/badge'
import { Button } from '@/shared/components/ui/button'
import {
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from '@/shared/components/ui/card'
import { ConfirmationDialog } from '@/shared/components/ui/confirmation-dialog'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/shared/components/ui/table'
import { toast } from '@/shared/lib/toast'
import { useAddon, useRemoveCharge } from '../../hooks/useAddons'
import { OneTimeChargeDrawer } from './OneTimeChargeDrawer'

interface OneTimeCostsTabProps {
  planId: string
}

export function OneTimeCostsTab({ planId }: OneTimeCostsTabProps) {
  const { data: plan, isLoading } = useAddon(planId)
  const removeCharge = useRemoveCharge()

  // Drawer state
  const [drawerOpen, setDrawerOpen] = useState(false)
  const [editingCharge, setEditingCharge] = useState<PlanCharge | null>(null)
  const [drawerMode, setDrawerMode] = useState<'create' | 'edit'>(
    DRAWER_MODE.create
  )

  // Delete confirmation dialog state
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false)
  const [chargeToDelete, setChargeToDelete] = useState<PlanCharge | null>(null)

  const handleAddClick = () => {
    setEditingCharge(null)
    setDrawerMode(DRAWER_MODE.create)
    setDrawerOpen(true)
  }

  const handleEditClick = (charge: PlanCharge) => {
    setEditingCharge(charge)
    setDrawerMode(DRAWER_MODE.edit)
    setDrawerOpen(true)
  }

  const handleDeleteClick = (charge: PlanCharge) => {
    setChargeToDelete(charge)
    setDeleteDialogOpen(true)
  }

  const handleDeleteConfirm = async () => {
    if (!chargeToDelete) return

    try {
      await removeCharge.mutateAsync({
        planId,
        chargeId: chargeToDelete.id,
      })
      toast.success('One-time charge removed successfully')
      setDeleteDialogOpen(false)
      setChargeToDelete(null)
    } catch {
      // Error toast is handled by the mutation hook
    }
  }

  // Filter only one-time charges
  const oneTimeCharges =
    plan?.charges?.filter(c => c.charge_type === CHARGE_TYPE.one_time) || []

  return (
    <>
      <Card className='bg-transparent border-none shadow-none'>
        <CardHeader>
          <div className='flex items-center justify-between'>
            <div>
              <CardTitle className='flex items-center gap-2'>
                <CreditCard className='w-5 h-5' />
                One-Time Costs
              </CardTitle>
              <p className='text-sm text-muted-foreground mt-1'>
                One-time charges that apply when a customer subscribes to your
                addon
              </p>
            </div>
            <Button type='button' size='sm' onClick={handleAddClick}>
              <Plus className='w-4 h-4 mr-2' />
              Add One-Time Cost
            </Button>
          </div>
        </CardHeader>
        <CardContent>
          {isLoading ? (
            <div className='flex items-center justify-center py-12'>
              <Loader2 className='w-6 h-6 animate-spin mr-2 text-muted-foreground' />
              <span className='text-muted-foreground'>Loading charges...</span>
            </div>
          ) : oneTimeCharges.length === 0 ? (
            <div className='text-center py-12 border border-dashed rounded-lg bg-muted/20'>
              <CreditCard className='w-12 h-12 mx-auto mb-4 text-muted-foreground opacity-50' />
              <p className='text-muted-foreground mb-4'>
                No one-time charges configured yet
              </p>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={handleAddClick}
              >
                <Plus className='w-4 h-4 mr-2' />
                Add Your First One-Time Cost
              </Button>
            </div>
          ) : (
            <div className='border rounded-lg overflow-hidden'>
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Charge Name</TableHead>
                    <TableHead>Code</TableHead>
                    <TableHead>Amount</TableHead>
                    <TableHead>Description</TableHead>
                    <TableHead className='text-right'>Actions</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {oneTimeCharges.map(charge => (
                    <TableRow key={charge.id}>
                      <TableCell className='font-medium'>
                        {charge.charge_name}
                      </TableCell>
                      <TableCell>
                        <Badge variant='outline' className='font-mono text-xs'>
                          {charge.charge_code}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center gap-1 text-sm'>
                          <DollarSign className='w-3 h-3 text-muted-foreground' />
                          <span className='font-medium'>
                            {charge.amount.toFixed(2)}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell>
                        <span className='text-sm text-muted-foreground'>
                          {charge.description || '-'}
                        </span>
                      </TableCell>
                      <TableCell>
                        <div className='flex items-center justify-end gap-2'>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => handleEditClick(charge)}
                          >
                            <Edit className='w-4 h-4' />
                          </Button>
                          <Button
                            type='button'
                            variant='ghost'
                            size='sm'
                            onClick={() => handleDeleteClick(charge)}
                            disabled={removeCharge.isPending}
                          >
                            <Trash2 className='w-4 h-4 text-destructive' />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* One-Time Charge Drawer */}
      <OneTimeChargeDrawer
        planId={planId}
        open={drawerOpen}
        onOpenChange={setDrawerOpen}
        editingCharge={editingCharge ?? undefined}
        mode={drawerMode}
      />

      {/* Delete Confirmation Dialog */}
      <ConfirmationDialog
        open={deleteDialogOpen}
        onOpenChange={setDeleteDialogOpen}
        title='Delete One-Time Charge'
        description='Are you sure you want to remove this charge from the addon? This action cannot be undone.'
        confirmText='Delete'
        cancelText='Cancel'
        variant='destructive'
        onConfirm={handleDeleteConfirm}
        onCancel={() => setChargeToDelete(null)}
      />
    </>
  )
}
